import { api } from '@/lib/api'
import { useAsync } from '@/lib/useAsync'
import { Field, Select } from './ui/form'
import type { ReferenceItem, ReferenceList } from '@/types/api'

/**
 * A managed picklist rendered as a labelled dropdown.
 *
 * <p>
 * The list is fetched per country, because the API only offers what that country may
 * pick - a Malaysian legal form is not an answer for a Singapore customer.
 * </p>
 *
 * The entry a record already carries is added back to the options when it is not in the
 * live list. An entry retired last year is still the truth about a customer created
 * before it was retired, and a picker that silently drops it makes the field read as
 * empty and the next save wipe it.
 */
export function PicklistField({
  label,
  hint,
  list,
  countryId,
  value,
  valueName,
  onChange,
  disabled,
  changed,
}: {
  label: string
  hint?: string
  list: ReferenceList
  countryId: string | undefined
  value: string | null
  /** The name as the record stores it, used when the entry is no longer offered. */
  valueName?: string | null
  onChange: (id: string | null) => void
  disabled?: boolean
  changed?: boolean
}) {
  const items = useAsync<ReferenceItem[]>(
    (signal) =>
      countryId
        ? api.get<ReferenceItem[]>(`/reference/${list}`, { countryId }, signal)
        : Promise.resolve([]),
    [list, countryId],
  )

  const options = items.data ?? []
  const missing = value !== null && !options.some((item) => item.id === value)

  return (
    <Field label={label} hint={hint} changed={changed}>
      {(id) => (
        <Select
          id={id}
          disabled={disabled || items.loading}
          value={value ?? ''}
          onChange={(event) => onChange(event.target.value || null)}
        >
          <option value="">Not set</option>

          {missing && (
            <option value={value}>{valueName ? `${valueName} (retired)` : 'Current entry'}</option>
          )}

          {options.map((item) => (
            <option key={item.id} value={item.id}>
              {item.name}
            </option>
          ))}
        </Select>
      )}
    </Field>
  )
}
